import React, {
  ComponentType,
  useState
} from 'react'
import {
  Button,
  Form,
  Icon,
  Message,
  Popup
} from 'semantic-ui-react'
// Local
import { AUTH_TYPE, shouldRenderReskin2025 } from '../../constant'
import If from '../common/if'
import ReveleerCoachingBox from '../ReveleerCoachingBox'

const SafeForm = Form as unknown as ComponentType<any>

type TotpMfaFormProps = {
  id: string
  onSubmit: (
    mfaCode: string,
    email: string,
    username: string,
    password: string,
    rememberMe: string,
    authType: string,
    qrCodeUuid: string
  ) => void
  email: string
  username: string
  password: string
  errorMessage: string | null
  qrCodeUuid: string
}

/**
 * Provides an MFA form. Assumes that if the UI is showing the MFA code entry
 * form than the user has completed
 * @param id An ID attribute prefix used to identify component elements
 * @param onSubmit Login form submit callback function
 *        Arguments: MFA code, username, password, rememberMe
 * @param email Optional default email address
 * @param username Optional default username
 * @param password Optional default password
 * @param errorMessage Optional default error message
 */
function TotpMfaForm(
  props: TotpMfaFormProps
): JSX.Element {
  const {
    id,
    onSubmit,
    email: emailProp,
    username: usernameProp,
    password: passwordProp,
    errorMessage,
    qrCodeUuid
  } = props

  const [ email ] = useState(emailProp || '')
  const [ username ] = useState(usernameProp || '')
  const [ password ] = useState(passwordProp || '')
  const [
    mfaCode,
    setMfaCode
  ] = useState('')
  const [
    shouldRememberMe,
    setShouldRememberMe
  ] = useState(true)
  const [
    isLoading,
    setIsLoading
  ] = useState(false)

  const validateMfaCode = (code: string) => {
    if (code) {
      return code.length === 6 && /^\d+$/.test(code)
    }
    return false
  }

  const handleSubmit = () => {
    if (username.length > 0 && password.length > 0 && validateMfaCode(mfaCode)) {
      setIsLoading(true)
      onSubmit(mfaCode, email, username, password, String(shouldRememberMe), AUTH_TYPE.TOTP, qrCodeUuid)
    }
  }

  const handleChangeMfaCode = (
    event: React.ChangeEvent,
    { value }: { value: string }
  ) => {
    setMfaCode(value)
  }

  const toggleShouldRememberMe = (isChecked: boolean) => {
    setShouldRememberMe(isChecked)
  }

  const rememberMePopupContent = 'By selecting this box, we will wait' +
    ' 30 days before we confirm your identity again. Do not select' +
    ' this option if this computer is used by multiple people.'

  return (
    <div
      id={ `mfa-${id}-login-container` }
      className="m-2-top"
    >
      <If isTrue={ !!errorMessage }>
        <If isTrue={ !shouldRenderReskin2025 }>
          <Message
            error={ true }
            content={ errorMessage }
          />
        </If>
        <If isTrue={ shouldRenderReskin2025 }>
          <ReveleerCoachingBox
            coachingBoxType="error"
            isShowing
            message={
              <p>{ errorMessage }</p>
            }
          />
        </If>
      </If>
      <div className="mfa-login-form">
        <div className="m-1-top">
          <h2>Verify Your Identity</h2>
        </div>
        <div className="mfa-login-form-header m-1-top">
          For patient protection, additional verification is needed before
          you can continue.
        </div>
        <div className="mfa-login-form-header m-1-top">
          <span>Please generate your one-time use verification code using your authenticator app and enter the code below.</span>
        </div>
        <div className="m-1-top">
          <SafeForm
            onSubmit={ handleSubmit }
            error={ errorMessage != null }
          >
            <Form.Group inline>
              <Form.Input
                id={ `mfa-totp-${id}-login-code-input` }
                label="Verification Code:"
                placeholder="MFA Code"
                onChange={ handleChangeMfaCode }
                name="mfaCode"
                value={ mfaCode }
              />
              <div className="m-1-top">
                <Button
                  id={ `mfa-totp-${id}-login-submit` }
                  color="blue"
                  type="submit"
                  disabled={ !validateMfaCode(mfaCode) || isLoading }
                >
                  Verify
                </Button>
              </div>
            </Form.Group>
            <div>
              <Form.Group>
                <Form.Checkbox
                  id={ `mfa-totp-${id}-login-remember-me` }
                  label="Remember Me"
                  onChange={
                    (
                      event: any,
                      data: { checked: boolean }
                    ) => {
                      toggleShouldRememberMe(data.checked)
                    }
                  }
                  checked={ shouldRememberMe }
                />
                <Popup
                  content={ rememberMePopupContent }
                  mouseEnterDelay={ 500 }
                  trigger={
                    <Icon
                      name="info circle"
                      className="pointer"
                    />
                  }
                />
              </Form.Group>
            </div>
          </SafeForm>
        </div>
      </div>
    </div>
  )
}

export default TotpMfaForm
